/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.15.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { IOnboardingReducer } from './onboardingReducer';
import {
    EOnboardingComponentsId,
    EOnboardingModalId,
    EOnboardingPage,
    EOnboardingStatus,
} from './types/onboardingTypes';

export const getOnboardingState = (state: IRootReducer) => state.onboarding;

export const makeSelectOnboardingModalCurrentStep = ():
    OutputSelector<IRootReducer, EOnboardingComponentsId | undefined,
    (state: IOnboardingReducer) => EOnboardingComponentsId | undefined> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.currentStep,
    );

export const makeSelectOnboardingModalFinishId = (): OutputSelector<IRootReducer, EOnboardingModalId | undefined,
    (state: IOnboardingReducer) => EOnboardingModalId | undefined> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.modalFinalId,
    );

export const makeSelectOnboardingModalStartId = (): OutputSelector<IRootReducer, EOnboardingModalId | undefined,
    (state: IOnboardingReducer) => EOnboardingModalId | undefined> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.modalStartId,
    );

export const makeSelectOnboardingPage = (): OutputSelector<IRootReducer, EOnboardingPage,
    (state: IOnboardingReducer) => EOnboardingPage> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.page,
    );

export const makeSelectOnboardingStatus = (): OutputSelector<IRootReducer, EOnboardingStatus,
    (state: IOnboardingReducer) => EOnboardingStatus> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.status,
    );

export const makeSelectOnboardingIsActive = (): OutputSelector<IRootReducer, boolean,
    (state: IOnboardingReducer) => boolean> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.status === EOnboardingStatus.start
            || state.status === EOnboardingStatus.pageReady,
    );


export const makeSelectOnboardingSteps = (): OutputSelector<IRootReducer, EOnboardingComponentsId[],
    (state: IOnboardingReducer) => EOnboardingComponentsId[]> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.stepsIds,
    );

export const makeSelectOnboardingIsOnboardingExists = (): OutputSelector<IRootReducer, boolean,
    (state: IOnboardingReducer) => boolean> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.isOnboardingExists,
    );

export const makeSelectOnboardingIsWelcomeOnboardingCompleted = (): OutputSelector<IRootReducer, boolean,
    (state: IOnboardingReducer) => boolean> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.isWelcomeOnboardingCompleted,
    );

export const makeSelectOnboardingIsDisplayedByUser = (): OutputSelector<IRootReducer, boolean,
    (state: IOnboardingReducer) => boolean> =>
    createSelector(
        getOnboardingState,
        (state: IOnboardingReducer) => state.isDisplayedByUser,
    );
